import { useEffect, useMemo, useState } from 'react'
import { Button } from 'blaze-ui/Button'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { TopBar } from '../TopBar/TopBar'
import { useStepAction } from '../useStepAction'
import { useVisualStyles } from 'hooks/queries/visualStyles'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useUpdateBrandKit } from 'hooks/queries/brandKits/useUpdateBrandKit'
import { useUpdateBrandVisualKit } from 'hooks/queries/workspaces/useUpdateBrandVisualKit'
import { useDisplayedFontPairingsAndVisualStyles } from '../BrandVisualStyleSelection/useDisplayedFontPairingsAndVisualStyles'
import { StyleGenerated } from '../BrandVisualStyleSelection/GenerationStage'
import { FontPairing } from 'hooks/queries/fontPairings'
import { BusinessType, VisualStyle } from 'types'
import { V4StyleCard } from './V4StyleCard'
import { BeforeAfterSlider } from './BeforeAfterSlider'
import { getBeforeImage, getAfterImage } from './visualStyleImages'
import { reportError } from 'utils/error'
import Styles from './V4VisualStyleSelection.module.scss'

export interface V4VisualStyleSelectionProps {
  onNext: (selectedVisualStyle: StyleGenerated, fontPairings?: FontPairing[]) => void
  onBack: () => void
  brandKitId?: number
  initialSelectedStyleId?: number
  businessType?: BusinessType
}

const toStyleGenerated = (s: VisualStyle): StyleGenerated => ({ ...s, styleUrl: s.cover_image_url })

export const V4VisualStyleSelection = ({
  onNext,
  onBack,
  brandKitId,
  initialSelectedStyleId,
  businessType,
}: V4VisualStyleSelectionProps) => {
  const { data: brandKitData } = useBrandKit(brandKitId, { enabled: !!brandKitId })
  const brandVisualKitId = brandKitData?.brand_kit?.brand_visual_kit?.id
  const { mutateAsync: updateBrandKit } = useUpdateBrandKit({ id: brandKitId ?? 0 })
  const { mutateAsync: updateBrandVisualKit } = useUpdateBrandVisualKit({
    id: brandVisualKitId ?? 0,
  })

  const { data: visualStyles } = useVisualStyles()
  const {
    displayedVisualStyles,
    displayedFontPairings,
    isLoading: isLoadingRecommendations,
  } = useDisplayedFontPairingsAndVisualStyles(brandKitId)

  const recommendedStyles = displayedVisualStyles

  const allOtherStyles = useMemo(() => {
    if (!visualStyles) return []
    const recommendedIds = new Set(recommendedStyles.map((s) => s.id))
    return visualStyles.filter((s) => !recommendedIds.has(s.id))
  }, [visualStyles, recommendedStyles])

  const [selectedId, setSelectedId] = useState<number | undefined>(initialSelectedStyleId)

  useEffect(() => {
    if (selectedId != null) return
    if (isLoadingRecommendations) return
    const firstId = recommendedStyles[0]?.id ?? visualStyles?.[0]?.id
    if (firstId != null) setSelectedId(firstId)
  }, [selectedId, isLoadingRecommendations, recommendedStyles, visualStyles])

  const selectedStyle = useMemo(() => {
    if (!selectedId) return undefined
    return [...(recommendedStyles ?? []), ...(allOtherStyles ?? [])].find((s) => s.id === selectedId)
  }, [selectedId, recommendedStyles, allOtherStyles])

  const { isSaving, run } = useStepAction()

  const handleNext = () => {
    if (!selectedStyle) return
    if (brandKitId && !brandVisualKitId) return
    const generated = toStyleGenerated(selectedStyle)
    run(async () => {
      try {
        if (brandKitId) {
          await Promise.all([
            updateBrandKit({
              content_preferences: {
                ...brandKitData?.brand_kit?.content_preferences,
                apply_visual_style: true,
                image_media_priority: 'only_my_brand_kit',
              },
            }),
            updateBrandVisualKit({ visual_style_id: selectedStyle.id }),
          ])
        }
        onNext(generated, displayedFontPairings)
      } catch (err) {
        reportError(err)
        throw err
      }
    }, 'Something went wrong saving your visual style. Please try again.')
  }

  return (
    <MultiStepStepLayout headingLevel={1} scrollContainerClassName={Styles.root} contentClassName={Styles.contentReset}>
      <div className={Styles.page}>
        <TopBar highlightStep="content" />
        <div className={Styles.columns}>
          <div className={Styles.leftCol}>
            <div className={Styles.scrollArea}>
              <div className={Styles.panelHeader}>
                <h1 className={Styles.heading}>Choose a visual style for your content</h1>
                <p className={Styles.subtitle}>
                  Blaze applies a consistent photo treatment — lighting, color, and texture — across everything it
                  creates. You can change this at any time.
                </p>
              </div>

              {recommendedStyles.length > 0 && (
                <section className={Styles.section}>
                  <span className={Styles.sectionLabel}>Recommended for your brand</span>
                  <div className={Styles.cardList}>
                    {recommendedStyles.map((style) => (
                      <V4StyleCard
                        key={style.id}
                        style={style}
                        isSelected={selectedId === style.id}
                        onSelect={setSelectedId}
                      />
                    ))}
                  </div>
                </section>
              )}

              {allOtherStyles.length > 0 && (
                <section className={Styles.section}>
                  <span className={Styles.sectionLabel}>All Styles</span>
                  <div className={Styles.cardList}>
                    {allOtherStyles.map((style) => (
                      <V4StyleCard
                        key={style.id}
                        style={style}
                        isSelected={selectedId === style.id}
                        onSelect={setSelectedId}
                      />
                    ))}
                  </div>
                </section>
              )}
            </div>

            <div className={Styles.footer}>
              <Button variant="tertiary" size="lg" onPress={onBack}>
                Back
              </Button>
              <Button
                size="lg"
                onPress={handleNext}
                isDisabled={!selectedStyle || isSaving || (!!brandKitId && !brandVisualKitId)}
              >
                {isSaving ? 'Saving...' : `Continue with ${selectedStyle?.name ?? '…'}`}
              </Button>
            </div>
          </div>

          <div className={Styles.rightCol}>
            <BeforeAfterSlider
              beforeImage={getBeforeImage(businessType)}
              afterImage={
                getAfterImage(selectedStyle?.name ?? '', businessType) ?? selectedStyle?.cover_image_url ?? ''
              }
              afterLabel={selectedStyle?.name ?? ''}
            />
          </div>
        </div>
      </div>
    </MultiStepStepLayout>
  )
}
